import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import * as PropTypes from 'prop-types';
import React, { useRef } from 'react';
import FusePageSimpleSidebar from './FusePageSimpleSidebar';
import FusePageSimpleHeader from './FusePageSimpleHeader';

const headerHeight = 120;
const toolbarHeight = 64;
const drawerWidth = 299;

const useStyles = makeStyles((theme) => ({
	root: {
		display: 'flex',
		flexDirection: 'column',
		minHeight: '100%',
		position: 'relative',
		flex: '1 0 auto',
		height: 'auto',
	},
	innerScroll: {
		flex: '1 1 auto',
		height: '100%',
	},
	wrapper: {
		display: 'flex',
		flexDirection: 'row',
		flex: '1 1 auto',
		zIndex: 2,
		maxWidth: '100%',
		minWidth: 0,
		height: '100%',
		background: 'transparent',
	},
	header: {
		height: headerHeight,
		minHeight: headerHeight,
		display: 'flex',
		color: theme.palette.primary.contrastText,
		backgroundSize: 'cover',
		backgroundColor: 'transparent',
	},
	topBg: {
		position: 'absolute',
		left: 0,
		right: 0,
		top: 0,
		height: headerHeight,
		pointerEvents: 'none',
	},
	contentWrapper: {
		display: 'flex',
		flexDirection: 'column',
		flex: '1 1 auto',
		overflow: 'auto',
		'-webkit-overflow-scrolling': 'touch',
		zIndex: 9999,
	},
	toolbar: {
		height: toolbarHeight,
		minHeight: toolbarHeight,
		display: 'flex',
		alignItems: 'center',
	},
	content: {
		flex: '1 0 auto',
		overflow: 'auto',
		width: '59vw',
	},
	sidebarWrapper: {
		overflow: 'hidden',
		backgroundColor: 'transparent',
		position: 'absolute',
		'&.permanent': {
			[theme.breakpoints.up('lg')]: {
				position: 'relative',
			},
		},
	},
	sidebar: {
		position: 'absolute',
		'&.permanent': {
			[theme.breakpoints.up('lg')]: {
				backgroundColor: theme.palette.background.default,
				color: theme.palette.text.primary,
				position: 'relative',
			},
		},
		width: drawerWidth,
		height: '100%',
	},
	leftSidebar: {
		[theme.breakpoints.up('lg')]: {
			borderRight: `1px solid ${theme.palette.divider}`,
			borderLeft: 0,
		},
	},
	rightSidebar: {
		[theme.breakpoints.up('lg')]: {
			borderRight: 0,
		},
		height: '100%',
		background: 'linear-gradient(130deg, rgba(244,249,255,1) 0%, rgba(208,226,244,1) 100%)',
	},
	sidebarHeader: {
		height: headerHeight,
		minHeight: headerHeight,
		backgroundColor: theme.palette.primary.dark,
		color: theme.palette.primary.contrastText,
	},
	sidebarHeaderInnerSidebar: {
		backgroundColor: 'transparent',
		color: 'inherit',
		height: 'auto',
		minHeight: 'auto',
	},
	sidebarContent: {},
	backdrop: {
		position: 'absolute',
	},
}));

const FusePageSimple = React.forwardRef((props, ref) => {
	// console.info("render::FusePageSimple");
	const leftSidebarRef = useRef(null);
	const rightSidebarRef = useRef(null);
	const rootRef = useRef(null);
	const classes = useStyles(props);

	React.useImperativeHandle(ref, () => ({
		rootRef,
		toggleLeftSidebar: () => {
			leftSidebarRef.current.toggleSidebar();
		},
		toggleRightSidebar: () => {
			rightSidebarRef.current.toggleSidebar();
		},
	}));

	return (
		<div className={clsx(classes.root, props.innerScroll && classes.innerScroll)} ref={rootRef}>
			<div className={clsx(classes.header, classes.topBg)} />

			<div className="flex flex-auto flex-col  z-10 h-full">
				{props.header && props.sidebarInner ? <FusePageSimpleHeader classes={classes} header={props.header} /> : null}

				<div className={classes.wrapper}>
					{props.leftSidebarHeader || props.leftSidebarContent ? (
						<FusePageSimpleSidebar
							classes={classes}
							content={props.leftSidebarContent}
							header={props.leftSidebarHeader}
							innerScroll={props.innerScroll}
							position="left"
							ref={leftSidebarRef}
							rootRef={rootRef}
							sidebarInner={props.sidebarInner}
							variant={props.leftSidebarVariant || 'permanent'}
						/>
					) : null}

					{props.header && !props.sidebarInner ? (
						<FusePageSimpleHeader classes={classes} header={props.header} />
					) : null}

					{props.contentToolbar ? <div className={classes.toolbar}>{props.contentToolbar}</div> : null}

					{props.content ? <div className={classes.content}>{props.content}</div> : null}
					{props.rightSidebarHeader || props.rightSidebarContent ? (
						<FusePageSimpleSidebar
							classes={classes}
							content={props.rightSidebarContent}
							header={props.rightSidebarHeader}
							innerScroll={props.innerScroll}
							position="right"
							ref={rightSidebarRef}
							rootRef={rootRef}
							sidebarInner={props.sidebarInner}
							variant={props.rightSidebarVariant || 'permanent'}
						/>
					) : null}
				</div>
			</div>
		</div>
	);
});

FusePageSimple.propTypes = {
	leftSidebarHeader: PropTypes.node,
	leftSidebarContent: PropTypes.node,
	leftSidebarVariant: PropTypes.node,
	rightSidebarHeader: PropTypes.node,
	rightSidebarContent: PropTypes.node,
	rightSidebarVariant: PropTypes.node,
	header: PropTypes.node,
	content: PropTypes.node,
	contentToolbar: PropTypes.node,
	sidebarInner: PropTypes.bool,
	innerScroll: PropTypes.bool,
};
FusePageSimple.displayName = 'FusePageSimple';

export default React.memo(FusePageSimple);
